import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DayTimeline } from "@/components/DayTimeline";
import { Letterhead } from "@/components/Letterhead";
import { Markdown } from "@/components/Markdown";
import { api, errText } from "@/lib/api";
import { fmtTime, isAllDay, isoDate, longDate, typeLabel, whenLabel } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";
import { useForumColors } from "@/context/ForumColorContext";

export default function PrintSheet() {
  const { user } = useAuth();
  const { colorFor, forumName } = useForumColors();
  const [params] = useSearchParams();
  const span = params.get("span") || "day";
  const date = params.get("date") || isoDate(new Date());
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      if (span === "day") {
        const [{ data }, todos] = await Promise.all([
          api.get("/events", { params: { start: date, end: date } }),
          api.get("/todos", { params: { date } }),
        ]);
        setRows([{ date, events: data, todos: todos.data }]);
      } else {
        const { data } = await api.get("/overview", { params: { span, date } });
        const days = data.days.filter((d) => d.total > 0);
        const first = days[0]?.date || date;
        const last = days[days.length - 1]?.date || date;
        const { data: todos } = await api.get("/todos", { params: { start: first, end: last } });
        setRows(
          days.map((d) => ({
            date: d.date,
            events: d.events,
            todos: todos.filter((t) => t.date === d.date),
          })),
        );
      }
    } catch (e) {
      toast.error(errText(e));
    }
  }, [span, date]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (rows) {
      const id = setTimeout(() => window.print(), 700);
      return () => clearTimeout(id);
    }
  }, [rows]);

  const title =
    span === "day" ? `Day sheet — ${longDate(date)}` : span === "week" ? "Week sheet" : "Month sheet";

  const legend = [];
  (rows || []).forEach((group) =>
    group.events.forEach((e) => {
      const name = forumName(e);
      if (!legend.some((l) => l.name === name)) legend.push({ name, color: colorFor(e) });
    }),
  );

  return (
    <div className="min-h-screen bg-white text-black" data-testid="print-sheet">
      <div className="mx-auto max-w-[820px] px-8 py-10">
        <Letterhead className="mb-5 border-primary/70" />
        <div className="flex items-start justify-between gap-6 border-b-2 border-black pb-4">
          <div>
            <h1 className="font-heading text-3xl font-bold">{title}</h1>
            <p className="mt-1 text-sm text-neutral-600">
              {user?.name} · Court Diary
              {span !== "day" && rows?.length ? ` · ${rows.length} sitting day(s)` : ""}
            </p>
          </div>
          <Button
            data-testid="print-now-btn"
            onClick={() => window.print()}
            className="rounded-full h-11 print:hidden"
          >
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
        </div>

        {legend.length ? (
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2" data-testid="print-legend">
            <span className="text-[0.7rem] uppercase tracking-widest text-neutral-500">
              Forum colours
            </span>
            {legend.map((l) => (
              <span
                key={l.name}
                data-testid={`print-legend-item-${l.name.replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-2 text-xs font-medium"
              >
                <span
                  className="inline-block h-3 w-3 border border-neutral-400"
                  style={{ background: l.color, printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
                />
                {l.name}
              </span>
            ))}
          </div>
        ) : null}

        {rows === null ? <p className="mt-8 text-sm">Preparing sheet…</p> : null}

        {span === "day" && rows?.[0]?.events?.length ? (
          <div className="mt-6" data-testid="print-day-timeline">
            <p className="text-[0.7rem] uppercase tracking-widest text-neutral-500 mb-2">
              The day at a glance
            </p>
            <DayTimeline events={rows[0].events} />
          </div>
        ) : null}

        {rows?.map((group) => (
          <section key={group.date} className="mt-8" data-testid={`print-group-${group.date}`}>
            {span !== "day" ? (
              <h2 className="font-heading text-lg font-semibold border-b border-neutral-400 pb-1 mb-3">
                {longDate(group.date)} · {group.events.length} listing(s)
              </h2>
            ) : null}
            {group.events.length === 0 ? (
              <p className="text-sm text-neutral-600">Nothing listed.</p>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-neutral-400">
                    <th className="py-2 pr-4 text-[0.7rem] uppercase tracking-widest w-32">When</th>
                    <th className="py-2 pr-4 text-[0.7rem] uppercase tracking-widest">Listing</th>
                    <th className="py-2 text-[0.7rem] uppercase tracking-widest w-56">Court / case</th>
                  </tr>
                </thead>
                <tbody>
                  {group.events.map((e) => (
                    <tr key={e.event_id} className="border-b border-neutral-300 align-top">
                      <td className="py-3 pr-4 text-sm font-semibold whitespace-nowrap">
                        <span
                          className="inline-block w-2 h-4 mr-2 align-middle border border-neutral-400"
                          style={{
                            background: colorFor(e),
                            printColorAdjust: "exact",
                            WebkitPrintColorAdjust: "exact",
                          }}
                        />
                        {whenLabel(e)}
                        {isAllDay(e) ? (
                          <span className="block ml-4 text-[0.65rem] uppercase tracking-widest">
                            {e.event_type === "out_of_station" ? "out of station" : "all day"}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3 pr-4">
                        <span className="block text-base font-semibold">{e.title}</span>
                        <span className="block text-xs text-neutral-600">
                          {typeLabel(e.event_type)}
                          {e.location ? ` · ${e.location}` : ""}
                        </span>
                        {(e.urgent || e.clash || !e.matter_id) && (
                          <span className="block text-xs font-semibold mt-0.5">
                            {[
                              e.urgent ? "URGENT" : null,
                              e.clash ? "CLASH — overlapping fixed slot" : null,
                              !e.matter_id ? "PLACEHOLDER" : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-xs text-neutral-700">
                        {e.listing_details ? (
                          <span className="block mb-1 text-sm text-black">
                            <strong>Listing: </strong>
                            <Markdown
                              text={e.listing_details}
                              inline
                              testId={`print-listing-details-${e.event_id}`}
                            />
                          </span>
                        ) : null}
                        {[e.court_name, e.case_number].filter(Boolean).join(" · ") || "—"}
                        <Markdown
                          text={e.notes}
                          className="mt-1 text-xs text-neutral-700"
                          testId={`print-notes-${e.event_id}`}
                        />
                        {e.vc_link ? (
                          <span className="block mt-1 break-all">
                            VC{e.vc_platform ? ` (${e.vc_platform})` : ""}: {e.vc_link}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {group.todos?.length ? (
              <div className="mt-5" data-testid={`print-todos-${group.date}`}>
                <p className="text-[0.7rem] uppercase tracking-widest text-neutral-500">
                  To-do · {group.todos.filter((t) => !t.done).length} open of {group.todos.length}
                </p>
                <ul className="mt-2 space-y-1.5">
                  {group.todos.map((t) => (
                    <li key={t.todo_id} className="flex items-start gap-2 text-sm">
                      <span className="font-mono">{t.done ? "[x]" : "[ ]"}</span>
                      <span>
                        <span className={t.done ? "line-through" : "font-medium"}>{t.title}</span>
                        {t.priority === "high" || t.time_hint || t.matter_title ? (
                          <span className="block text-xs text-neutral-600">
                            {[
                              t.priority === "high" ? "HIGH" : null,
                              t.time_hint,
                              t.matter_title,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>
        ))}

        <p className="mt-10 text-xs text-neutral-500 border-t border-neutral-300 pt-3">
          Overlapping listings are advisory only — Court Diary never blocks an entry.
        </p>
      </div>
    </div>
  );
}
